import React, { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { LifeBuoy, Info, Paperclip, X, ExternalLink } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { formatFileSize } from '@/services/attachments.service';
import {
  sendBugReport,
  SUPPORT_MAX_FILES,
  SUPPORT_MAX_FILE_SIZE,
  SUPPORT_MAX_TITLE_LENGTH,
  SUPPORT_MAX_DESCRIPTION_LENGTH,
} from '@/services/support.service';

interface SupportButtonProps {
  // Context suplimentar despre pagina curentă (ex. { projectId, tab }).
  context?: Record<string, unknown>;
  // Eticheta butonului flotant (afișată doar pe ecrane medii și mari).
  label?: string;
}

// Orice tip de fișier este permis (cerință: utilizatorul trebuie să poată
// trimite orice document ajută la reproducerea problemei). Limitele rămân
// cele de dimensiune/număr, aplicate și pe server.
const ACCEPTED_FILES = '';

const DESCRIPTION_PLACEHOLDER = [
  'Ce ați făcut: am apăsat butonul „Salvează” din fișa investiției.',
  'Ce vă așteptați să se întâmple: datele să fie salvate.',
  'Ce s-a întâmplat de fapt: a apărut un mesaj de eroare roșu.',
].join('\n');

// Butonul e montat global, în Layout, deci nu mai primește context din pagină.
// Deducem din URL ce se poate: ce fel de pagină e și, dacă există, id-ul proiectului
// și tabul de dosar (ex. /projects/6#activitati).
function deriveRouteContext(): Record<string, unknown> {
  const path = window.location.pathname;
  const hash = window.location.hash.replace(/^#/, '');
  const out: Record<string, unknown> = { ruta: path + (hash ? `#${hash}` : '') };
  const proj = path.match(/\/projects\/(\d+)/);
  if (proj) {
    out.projectId = Number(proj[1]);
    if (hash) out.tab = hash;
  }
  const sectiuni: Record<string, string> = {
    '/': 'Panou principal',
    '/proiecte/toate': 'Tabel proiecte',
    '/proiecte/coordonate': 'Proiecte coordonate',
    '/proiecte/in-lucru': 'Proiecte în lucru',
    '/panou-central': 'Panou central',
    '/lista-investitii': 'Lista de investiții',
    '/admin/lista-investitii': 'Administrare — Lista de investiții',
  };
  if (sectiuni[path]) out.sectiune = sectiuni[path];
  return out;
}

export default function SupportButton({ context, label = 'Suport' }: SupportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pagina curentă, citită la deschiderea modalului (transparență pentru utilizator).
  const pageInfo = useMemo(() => {
    if (!isOpen || typeof window === 'undefined') {
      return { title: '', path: '' };
    }
    return {
      title: document.title,
      path: `${window.location.pathname}${window.location.hash}`,
    };
  }, [isOpen]);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (isSending) return;
    setIsOpen(false);
  }, [isSending]);

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    // Golim input-ul ca să putem re-selecta același fișier după eliminare.
    event.target.value = '';
    if (selected.length === 0) return;

    const accepted: File[] = [];
    const tooBig: string[] = [];
    let refused = 0;

    selected.forEach((file) => {
      if (file.size > SUPPORT_MAX_FILE_SIZE) {
        tooBig.push(file.name);
        return;
      }
      if (files.length + accepted.length >= SUPPORT_MAX_FILES) {
        refused += 1;
        return;
      }
      accepted.push(file);
    });

    if (tooBig.length > 0) {
      toast.error(
        tooBig.length === 1
          ? `Fișierul „${tooBig[0]}” depășește limita de 10 MB și nu a fost adăugat.`
          : `${tooBig.length} fișiere depășesc limita de 10 MB și nu au fost adăugate.`
      );
    }
    if (refused > 0) {
      toast.error(`Puteți atașa maximum ${SUPPORT_MAX_FILES} fișiere.`);
    }
    if (accepted.length > 0) {
      setFiles((prev) => [...prev, ...accepted]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSending) return;

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle) {
      toast.error('Titlul este obligatoriu.');
      return;
    }
    if (!trimmedDescription) {
      toast.error('Descrierea este obligatorie.');
      return;
    }

    setIsSending(true);
    try {
      const result = await sendBugReport({
        title: trimmedTitle,
        description: trimmedDescription,
        pageUrl: window.location.href,
        pagePath: `${window.location.pathname}${window.location.hash}`,
        pageTitle: document.title,
        context: {
          ...deriveRouteContext(),
          ...(context || {}),
          screen: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          userAgent: navigator.userAgent,
          language: navigator.language,
        },
        files,
      });

      toast.success(
        (t) => (
          <span className="flex flex-col gap-1">
            <span>Raportul a fost trimis (#{result.number}).</span>
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 underline"
              onClick={() => toast.dismiss(t.id)}
            >
              Vezi raportul
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </span>
        ),
        { duration: 8000 }
      );

      if (result.attachmentsFailed && result.attachmentsFailed.length > 0) {
        toast.error(
          `Unele fișiere nu au putut fi atașate: ${result.attachmentsFailed.join(', ')}.`
        );
      }

      resetForm();
      setIsOpen(false);
    } catch (err) {
      // Păstrăm modalul deschis ca să nu se piardă textul scris.
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Nu am putut trimite raportul de suport. Încercați din nou.';
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* Buton flotant discret, colț dreapta-jos */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        title="Raportează o problemă"
        aria-label="Raportează o problemă"
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur border border-gray-300 text-gray-700 shadow-lg px-3 py-3 sm:px-4 transition-colors hover:bg-gray-50 hover:text-primary-700 hover:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        <LifeBuoy className="w-5 h-5 flex-shrink-0" />
        <span className="hidden sm:inline text-sm font-medium">{label}</span>
      </button>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Raportează o problemă"
        size="full"
        closeOnOverlayClick={!isSending}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={handleClose} disabled={isSending}>
              Anulează
            </Button>
            <Button
              type="submit"
              form="support-bug-report-form"
              isLoading={isSending}
              disabled={isSending}
            >
              {isSending ? 'Se trimite...' : 'Trimite raportul'}
            </Button>
          </div>
        }
      >
        <form id="support-bug-report-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Titlu"
            required
            value={title}
            maxLength={SUPPORT_MAX_TITLE_LENGTH}
            onChange={(e) => setTitle(e.target.value.replace(/[\r\n]/g, ''))}
            placeholder="Pe scurt, care este problema?"
            hint={`${title.length}/${SUPPORT_MAX_TITLE_LENGTH} caractere`}
            disabled={isSending}
          />

          <Textarea
            label="Descriere"
            required
            rows={7}
            value={description}
            maxLength={SUPPORT_MAX_DESCRIPTION_LENGTH}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={DESCRIPTION_PLACEHOLDER}
            hint={`Descrieți ce ați făcut, ce vă așteptați și ce s-a întâmplat. ${description.length}/${SUPPORT_MAX_DESCRIPTION_LENGTH} caractere`}
            disabled={isSending}
          />

          {/* Fișiere atașate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fișiere (opțional)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILES || undefined}
              onChange={handleFilesSelected}
              disabled={isSending || files.length >= SUPPORT_MAX_FILES}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-sm text-gray-500">
              Maximum {SUPPORT_MAX_FILES} fișiere, câte 10 MB fiecare. Capturile de ecran ajută cel mai mult.
            </p>

            {files.length > 0 && (
              <ul className="mt-3 space-y-2">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className="truncate text-sm text-gray-900">{file.name}</span>
                      <span className="flex-shrink-0 text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={isSending}
                      aria-label={`Elimină fișierul ${file.name}`}
                      className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-danger-600 disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Ce se trimite automat - informativ, read-only */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Info className="w-4 h-4 text-gray-400" />
              Se trimit automat
            </div>
            <dl className="mt-2 space-y-1 text-sm text-gray-600">
              <div className="flex gap-2">
                <dt className="flex-shrink-0 text-gray-500">Pagina:</dt>
                <dd className="min-w-0 break-words">{pageInfo.title || '(fără titlu)'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="flex-shrink-0 text-gray-500">Adresa:</dt>
                <dd className="min-w-0 break-all font-mono text-xs">{pageInfo.path}</dd>
              </div>
            </dl>
            <p className="mt-2 text-xs text-gray-500">
              Se atașează automat și utilizatorul autentificat, data raportării și detalii tehnice
              despre browser (rezoluție, versiune). Nu se trimit alte date.
            </p>
          </div>
        </form>
      </Modal>
    </>
  );
}
